import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton, Modal } from '@mui/material';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import { setMuac, setEstBmiMuac } from '../../store/slices/metaDataSlice';

import { getMUAC_BMI } from '../../shared/utils/alternativeMeasures';
import { ImageModal } from '../../shared/components/modal/ImageModal';
import { SingleNumberCard } from '../../shared/components/numberInput/numberCards/singleNumberCard';
import { InfoModal } from '../../shared/components/modal/InfoModal';
import InfoIcon from '../../assets/icons/infoIcon.svg';
import SlantArrowIcon from '../../assets/icons/slantArrowIcon.svg';
import BMIMUACImage from '../../assets/images/BMI-Muac.svg';
import BMIMUACImage2 from '../../assets/images/BMI-MUAC2.png';
import bmiIconWhite from '../../assets/icons/bmiIconWhite.svg';
import { BMICard } from '../../shared/components/bmiCard';
import { AddOnInfoModal } from '../../shared/components/modal/addOnInfoModal';

export const Muac = () => {
  const dispatch = useDispatch();
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const { alternativeMeasures } = useAppSelector(metaDataSelector);
  const { muac, estBMI_muac } = alternativeMeasures;

  useEffect(() => {
    dispatch(setEstBmiMuac(getMUAC_BMI(muac)));
  }, [muac]);

  return (
    <>
      <Box
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          padding: 2,
          borderRadius: 0,
          backgroundColor: '#f9f9f9',
          marginTop: '-19px'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontFamily: 'Calibri',
              fontSize: '1rem',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '1.25rem',
              textAlign: 'left'
            }}
          >
            Estimating BMI category from Mid Upper Arm Circumference (MUAC) -
            used if unable to obtain weight/height to establish a likely BMI
            range.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <IconButton
              onClick={() => setOpenInfoModal(true)}
              size="small"
              aria-label="info"
              data-testid="muacInfoPopupButton"
            >
              <img src={InfoIcon} alt="Info" width="20" height="20" />
            </IconButton>
          </Box>
        </Box>
        <ol
          style={{
            fontFamily: 'Calibri',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '1.25rem',
            textAlign: 'left',
            marginTop: '1.25rem',
            paddingLeft: '0.9375rem'
          }}
        >
          <li>Hold the left arm at 90 degree angle with palm upwards.</li>
          <li>
            Mark the mid-point between the acromial process of the scapula (bony
            protrusion surface of the upper shoulder) and the olecranon process
            of the elbow (bony point of the elbow).
          </li>
          <li>Allow arm to hang loose and measure circumference of arm.</li>
          <li>Record MUAC measure below.</li>
        </ol>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '1.25rem'
          }}
        >
          <Button
            variant="outlined"
            sx={{
              marginBottom: 2,
              height: '2.5rem',
              padding: '0 0.9375rem',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              textAlign: 'center',
              lineHeight: 'normal',
              borderRadius: '100px',
              backgroundColor: '#007ACC',
              color: '#FFF'
            }}
            onClick={() => setOpenImageModal(true)}
            data-testid="muacImagePopupButton"
          >
            Click for image
            <Box
              sx={{
                height: '1.25rem',
                width: '1.25rem',
                backgroundColor: '#FFF',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: '0.625rem'
              }}
            >
              <img
                src={SlantArrowIcon}
                alt="icon"
                style={{
                  height: '60%',
                  width: '60%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <SingleNumberCard
            width={'50%'}
            title={'MUAC'}
            marginVal={'0.625rem'}
            subHead={'(cm)'}
            value={Number(muac)}
            setValue={(val: number) => dispatch(dispatch(setMuac(Number(val))))}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <BMICard
            icon={bmiIconWhite}
            label1="BMI"
            label2={estBMI_muac}
            unit={'kg/m<sup>2</sup>'}
          />
        </Box>
      </Box>
      <ImageModal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        srcImage={BMIMUACImage}
        imageHeight="85vh"
        srcImage2={BMIMUACImage2}
        imageHeight2="85vh"
        dialogPadding="0 0 1.5625rem 0"
        closeButtonMarginRight="0.3125rem"
        closeButtonMarginTop="0.3125rem"
      />
      {/* <InfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        title={'Reference: '}
        content={
          'Irish Nutrition and Dietetic Institute (2022) Nutrition Support Reference Guide.'
        }
      /> */}
      <Modal
        open={openInfoModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`BMI`}
          description1={` = Body Mass Index`}
          title={`Reference:`}
          content={`With permissions from Malnutrition Advisory Group, a Standing Committee of the British Associated of Parenteral and Enteral Nutrition. (2003)`}
          onClose={() => setOpenInfoModal(false)}
        />
      </Modal>
    </>
  );
};
